/**
 * useHostBranding — PRE-AUTH tenant branding resolved from the page hostname.
 *
 * Counterpart to useOrgBranding (which reads the logged-in profile): before
 * anyone is authenticated the only tenant signal we have is the domain the
 * page is served from, so this hook asks the public API to resolve the
 * hostname's first DNS label (e.g. "hglms" from hglms.yudulearning.com) via
 * organizations.subdomain:
 *
 *   GET /api/public/org-branding?host=<hostname>
 *     → { branding: { org_name, logo_url, auth_image_url } | null }
 *
 * Unknown hosts (localhost, Vercel previews, tenants without a dedicated
 * domain) resolve to null fields and callers keep the neutral Yudu defaults.
 *
 * The last successful answer is cached in localStorage per hostname (10 min
 * TTL) and used as the INITIAL state, so repeat visitors see their tenant's
 * branding on first paint instead of a neutral→branded flash; the network
 * refresh then reconciles in the background. Fetch errors fail soft: keep
 * whatever we have (cached or neutral), never block the login flow.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { queryClient } from '../services/queryClient';

export interface HostBranding {
  /** Tenant logo URL, or null → caller renders the neutral Yudu logo. */
  logoUrl: string | null;
  /** Left-side auth illustration URL, or null → caller renders the default art. */
  authImageUrl: string | null;
  /**
   * Dedicated browser-tab icon URL (a square emblem, distinct from the wide
   * login logo), or null → the tab falls back to the logo, then neutral.
   */
  faviconUrl: string | null;
  /** Tenant display name (e.g. for document.title), or null when unbranded. */
  orgName: string | null;
}

const NEUTRAL: HostBranding = { logoUrl: null, authImageUrl: null, faviconUrl: null, orgName: null };

const CACHE_TTL_MS = 10 * 60 * 1000;
const cacheKey = (host: string) => `host-branding:v1:${host}`;

function readCache(host: string): HostBranding | null {
  try {
    const raw = localStorage.getItem(cacheKey(host));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: HostBranding; t?: number };
    if (typeof parsed.t !== 'number' || Date.now() - parsed.t > CACHE_TTL_MS) return null;
    return parsed.v ?? null;
  } catch {
    return null; // corrupt entry / storage unavailable (e.g. private mode)
  }
}

function writeCache(host: string, value: HostBranding): void {
  try {
    localStorage.setItem(cacheKey(host), JSON.stringify({ v: value, t: Date.now() }));
  } catch {
    // Storage full/unavailable — caching is best-effort only.
  }
}

interface OrgBrandingResponse {
  branding?: {
    org_name?: string | null;
    logo_url?: string | null;
    auth_image_url?: string | null;
    favicon_url?: string | null;
  } | null;
}

async function fetchHostBranding(host: string): Promise<HostBranding> {
  const { data } = await api.get<OrgBrandingResponse>('/api/public/org-branding', { params: { host } });

  const b = data?.branding;

  const next: HostBranding = b
    ? {
        logoUrl: b.logo_url ?? null,
        authImageUrl: b.auth_image_url ?? null,
        faviconUrl: b.favicon_url ?? null,
        orgName: b.org_name ?? null,
      }
    : NEUTRAL;

  writeCache(host, next);

  return next;
}

export const hostBrandingQueryKey = (host: string) => ['hostBranding', host];

export function useHostBranding(): HostBranding {
  const host = window.location.hostname.toLowerCase();
  const [cached] = useState<HostBranding>(() => readCache(host) ?? NEUTRAL);

  const { data } = useQuery(
    {
      queryKey: hostBrandingQueryKey(host),
      queryFn: () => fetchHostBranding(host),
      retry: false,
    },
    queryClient,
  );

  return data ?? cached;
}
